import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Minus, Plus, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import SEO from '../components/SEO';

// Fallback products in case API fails
const FALLBACK_PRODUCTS = {
  'oridzin-classic': {
    id: "oridzin-classic",
    name_sr: "ORIGIN Classic",
    name_en: "ORIGIN Classic",
    description_sr: "Klasičan suvi džin sa bogatom aromom borovice",
    description_en: "Classic dry gin with rich juniper aroma",
    long_description_sr: "ORIGIN Classic je naš flagship proizvod - klasičan suvi džin koji odražava svu veštinu i strast Destilerije Kovačić. Proizveden u malim serijama, sa pažljivo odabranim botaničkim sastojcima.\n\nDominantna nota borovice savršeno je uravnotežena sa suptilnim citrusnim i začinskim tonovima, stvarajući sofisticiran i elegantan profil ukusa koji će oduševiti i najzahtevnije ljubitelje džina.",
    long_description_en: "ORIGIN Classic is our flagship product - a classic dry gin that reflects all the skill and passion of Destilerija Kovačić. Produced in small batches with carefully selected botanical ingredients.\n\nThe dominant juniper note is perfectly balanced with subtle citrus and spicy tones, creating a sophisticated and elegant flavor profile that will delight even the most demanding gin lovers.",
    price: 4200,
    abv: 43,
    volume: "700ml",
    image_url: "https://customer-assets.emergentagent.com/job_destilerija/artifacts/9mrnwy6e_Dzin-Classic-new.webp",
    aroma_sr: "Intenzivna borovica sa suptilnim tonovima korijandra i citrusa",
    aroma_en: "Intense juniper with subtle notes of coriander and citrus",
    taste_sr: "Suv, čist, sa bogatim biljnim karakterom i dugim završetkom",
    taste_en: "Dry, clean, with rich herbal character and long finish",
    serving_sr: "Sa premium tonikom, kriškom limuna i zrnom borovice",
    serving_en: "With premium tonic, lemon slice and juniper berry"
  },
  'oridzin-citrus': {
    id: "oridzin-citrus",
    name_sr: "ORIGIN Citrus",
    name_en: "ORIGIN Citrus",
    description_sr: "Osvežavajući džin sa naglašenim citrus notama",
    description_en: "Refreshing gin with pronounced citrus notes",
    long_description_sr: "ORIGIN Citrus je naša interpretacija modernog džina - osvežavajuć i živahan, sa naglašenim citrusnim karakterom koji ga čini savršenim za letnje koktele.\n\nKombinacija limuna, pomorandže i bergamota daje mu jedinstven profil ukusa koji je istovremeno tradicionalan i inovativan.",
    long_description_en: "ORIGIN Citrus is our interpretation of modern gin - refreshing and lively, with a pronounced citrus character that makes it perfect for summer cocktails.\n\nThe combination of lemon, orange and bergamot gives it a unique flavor profile that is both traditional and innovative.",
    price: 4200,
    abv: 43,
    volume: "700ml",
    image_url: "https://customer-assets.emergentagent.com/job_destilerija/artifacts/2x2s1ysx_Dzin-Citrus-new.webp",
    aroma_sr: "Sveže note limuna i pomorandže sa blagim cvetnim akcentima",
    aroma_en: "Fresh lemon and orange notes with mild floral accents",
    taste_sr: "Osvežavajuć, citrusi dominiraju, sa glatkim završetkom",
    taste_en: "Refreshing, citrus-dominant, with smooth finish",
    serving_sr: "Sa tonikom, kriškom pomorandže i grančicom ruzmarina",
    serving_en: "With tonic, orange slice and rosemary sprig"
  },
  'oridzin-uv': {
    id: "oridzin-uv",
    name_sr: "ORIGIN UV",
    name_en: "ORIGIN UV",
    description_sr: "Jedinstven džin koji menja boju - od plave do ljubičaste",
    description_en: "Unique color-changing gin - from blue to purple",
    long_description_sr: "ORIGIN UV je naš najatraktivniji i najinovativniji proizvod. Ovaj jedinstven džin zahvaljujući prirodnom ekstraktu cveta Clitoria ternatea ima prepoznatljivu plavu boju koja se magično transformiše u ljubičastu pri kontaktu sa citrusima ili tonikom.\n\nPored vizuelnog spektakla, ORIGIN UV nudi i kompleksan profil ukusa sa notama borovice, lavande i suptilnih cvetnih aroma. Savršen izbor za one koji žele da impresioniraju goste ili jednostavno uživaju u jedinstvenom iskustvu.",
    long_description_en: "ORIGIN UV is our most attractive and innovative product. This unique gin, thanks to the natural extract of the Clitoria ternatea flower, has a distinctive blue color that magically transforms into purple when in contact with citrus or tonic.\n\nBesides the visual spectacle, ORIGIN UV also offers a complex flavor profile with notes of juniper, lavender and subtle floral aromas. Perfect choice for those who want to impress guests or simply enjoy a unique experience.",
    price: 4600,
    abv: 43,
    volume: "700ml",
    image_url: "https://customer-assets.emergentagent.com/job_destilerija/artifacts/7nbi26rk_Dzin-UV-new.webp",
    aroma_sr: "Cvetne note lavande i borovice sa egzotičnim akcentima",
    aroma_en: "Floral notes of lavender and juniper with exotic accents",
    taste_sr: "Elegantan, cvetni, sa misterioznim završetkom",
    taste_en: "Elegant, floral, with mysterious finish",
    serving_sr: "Sa tonikom i kriškom limuna - posmatrajte magičnu promenu boje!",
    serving_en: "With tonic and lemon slice - watch the magical color change!"
  }
};

// FAQ data for each product
const FAQ_DATA = {
  'oridzin-classic': [
    {
      question: 'Šta je Ориџин Classic?',
      answer: 'Ориџин Classic je premium craft džin iz Destilerije Kovačić, sa klasičnim, uravnoteženim biljnim notama i sofisticiranim ukusom.'
    },
    {
      question: 'Koje su dominantne arome u Ориџин Classic-u?',
      answer: 'Dominiraju arome borovice, korijandera, citrusa i blagih biljnih nota.'
    },
    {
      question: 'Da li Ориџин Classic sadrži šećer?',
      answer: 'Ne, Ориџин Classic je suvi džin i ne sadrži dodatne šećere.'
    },
    {
      question: 'Kako se Ориџін Classic najbolje pije?',
      answer: 'Najbolje se pije: sa tonikom i kriškom limuna ili limete, čist rashlađen sa ledom, ili u klasičnim koktelima poput gin & tonic, Negroni, Martini.'
    },
    {
      question: 'Da li je Ориџин Classic vegan?',
      answer: 'Da, Ориџин Classic je pogodan za vegane.'
    },
    {
      question: 'Gde mogu da kupim Ориџin Classic?',
      answer: 'Možete ga kupiti u vinotekama, specijalizovanim radnjama i online na sajtu Destilerije Kovačić.'
    },
    {
      question: 'Kako čuvati Ориџін Classic nakon otvaranja?',
      answer: 'Čuvati na tamnom mestu, na sobnoj temperaturi. Najbolje je potrošiti u roku od 12–24 meseca.'
    }
  ],
  'oridzin-citrus': [
    {
      question: 'Šta je Ориџін Citrus?',
      answer: 'Ориџін Citrus je osvežavajući craft džin iz Destilerije Kovačić sa naglašenim citrus notama.'
    },
    {
      question: 'Koje su dominantne arome u Ориџін Citrus-u?',
      answer: 'Dominiraju arome limuna, pomorandže i bergamota, uz blage biljne note koje balansiraju ukus.'
    },
    {
      question: 'Da li Ориџін Citrus sadrži šećer?',
      answer: 'Ne, Ориџін Citrus je suvi džin i ne sadrži dodatne šećere.'
    },
    {
      question: 'Kako se Ориџін Citrus najbolje pije?',
      answer: 'Najbolje se pije: sa tonikom i kriškom pomorandže, u koktelima kao što su gin & tonic, Tom Collins, Gin Fizz, ili kao osvežavajući aperitiv.'
    },
    {
      question: 'Da li je Ориџін Citrus vegan?',
      answer: 'Da, Ориџін Citrus je pogodan za vegane.'
    },
    {
      question: 'Gde mogu da kupim Ориџін Citrus?',
      answer: 'Možete ga kupiti u vinotekama, specijalizovanim radnjama i online na sajtu Destilerije Kovačić.'
    },
    {
      question: 'Kako čuvati Ориџін Citrus nakon otvaranja?',
      answer: 'Čuvati na tamnom mestu, na sobnoj temperaturi. Najbolje je potrošiti u roku od 12–24 meseca.'
    }
  ],
  'oridzin-uv': [
    {
      question: 'Šta je Ориџін UV?',
      answer: 'Ориџін UV je posebna, moderna varijanta Ориџін džina, poznata po svojoj jedinstvenoj boji i inovativnom pristupu.'
    },
    {
      question: 'Zašto je Ориџін UV specifičan?',
      answer: 'Ориџін UV je specifičan zbog svoje prirodne plave boje koja se menja u zavisnosti od kiselosti (pH) pića.'
    },
    {
      question: 'Od čega dolazi plava boja u Ориџін UV-u?',
      answer: 'Plava boja dolazi od Clitoria ternatea (butterfly pea flower). Ova biljka prirodno daje plavu boju.'
    },
    {
      question: 'Kako i zašto se boja menja?',
      answer: 'Boja se menja zbog promene pH vrednosti: uz dodatak limuna/limete boja prelazi u ljubičastu ili ružičastu, uz tonik boja može postati svetlije plava, bez dodataka ostaje intenzivno plava.'
    },
    {
      question: 'Da li Ориџін UV sadrži šećer?',
      answer: 'Ne, Ориџін UV je suvi džin i ne sadrži dodatne šećere.'
    },
    {
      question: 'Kako se Ориџін UV najbolje pije?',
      answer: 'Najbolje se pije: gin & tonic sa limunom/limetom (boja se menja), u modernim koktelima, ili kao "signature" piće u specijalnim kombinacijama.'
    },
    {
      question: 'Da li je Ориџін UV vegan?',
      answer: 'Da, Ориџін UV je pogodan za vegane.'
    },
    {
      question: 'Gde mogu da kupim Ориџін UV?',
      answer: 'Možete ga kupiti u vinotekama, specijalizovanim radnjama i online na sajtu Destilerije Kovačić.'
    },
    {
      question: 'Kako čuvati Ориџін UV nakon otvaranja?',
      answer: 'Čuvati na tamnom mestu, na sobnoj temperaturi. Najbolje je potrošiti u roku od 12–24 meseca.'
    }
  ]
};

// FAQ Item Component
function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-white/10">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-4 flex items-center justify-between text-left hover:text-gold transition-colors"
      >
        <span className="text-white/80 pr-4">{question}</span>
        <ChevronDown 
          size={20} 
          className={`text-gold flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 pb-4' : 'max-h-0'}`}>
        <p className="text-white/50 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    // Fetch product from PHP API, fallback to static data
    fetch(`/api/products.php?id=${id}`)
      .then(res => res.json())
      .then(data => {
        if (data && !data.error && data.id) {
          setProduct(data);
          // Track Meta Pixel ViewContent
          if (window.fbq) {
            window.fbq('track', 'ViewContent', {
              content_name: data.name_sr,
              content_ids: [data.id],
              content_type: 'product',
              value: data.price,
              currency: 'RSD'
            });
          }
        } else {
          // Use fallback
          const fallback = FALLBACK_PRODUCTS[id];
          if (fallback) {
            setProduct(fallback);
            if (window.fbq) {
              window.fbq('track', 'ViewContent', {
                content_name: fallback.name_sr,
                content_ids: [fallback.id],
                content_type: 'product',
                value: fallback.price,
                currency: 'RSD'
              });
            }
          }
        }
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching product:', err);
        // Use fallback on error
        const fallback = FALLBACK_PRODUCTS[id];
        if (fallback) {
          setProduct(fallback);
        }
        setLoading(false);
      });
  }, [id]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('sr-RS').format(price);
  };

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addItem(product);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center bg-[#0A0A0A]">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-32 flex flex-col items-center justify-center bg-[#0A0A0A]">
        <p className="text-white/40 mb-4">{t('error')}</p>
        <Link to="/shop" className="text-gold hover:text-gold-light">
          {t('back')}
        </Link>
      </div>
    );
  }

  const name = language === 'sr' ? product.name_sr : product.name_en;
  const description = language === 'sr' ? product.description_sr : product.description_en;
  const longDescription = language === 'sr' ? product.long_description_sr : product.long_description_en;
  const aroma = language === 'sr' ? product.aroma_sr : product.aroma_en;
  const taste = language === 'sr' ? product.taste_sr : product.taste_en;
  const serving = language === 'sr' ? product.serving_sr : product.serving_en;
  
  // Get FAQ from product or fallback to static data
  const faqText = language === 'sr' ? product.faq_sr : product.faq_en;
  
  // Parse FAQ text into array of {question, answer}
  const parseFAQ = (text) => {
    if (!text) return null;
    const lines = text.split('\n').filter(line => line.trim());
    const faqItems = [];
    let currentQuestion = null;
    
    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed.startsWith('P:') || trimmed.startsWith('Q:')) {
        currentQuestion = trimmed.substring(2).trim();
      } else if ((trimmed.startsWith('O:') || trimmed.startsWith('A:')) && currentQuestion) {
        faqItems.push({
          question: currentQuestion,
          answer: trimmed.substring(2).trim()
        });
        currentQuestion = null;
      }
    }
    return faqItems.length > 0 ? faqItems : null;
  };
  
  // Use parsed FAQ from database, fallback to static FAQ_DATA
  const faqItems = parseFAQ(faqText) || FAQ_DATA[id];

  // SEO for product page
  const seoTitle = name;
  const seoDescription = description;

  // Parse long description into sections for UV
  const renderLongDescription = () => {
    if (!longDescription) return null;
    
    // Split by section dividers
    const sections = longDescription.split('⸻').map(s => s.trim()).filter(s => s);
    
    if (sections.length <= 1) {
      // No sections, just render paragraphs
      return (
        <div className="space-y-4">
          {longDescription.split('\n\n').map((para, idx) => (
            <p key={idx} className="text-white/50 leading-relaxed">{para}</p>
          ))}
        </div>
      );
    }
    
    // Render sections with titles
    return (
      <div className="space-y-10">
        {sections.map((section, idx) => {
          const lines = section.split('\n\n').filter(l => l.trim());
          const title = lines[0];
          const content = lines.slice(1);
          
          return (
            <div key={idx}>
              {title && (
                <h3 className="font-heading text-xl md:text-2xl text-white mb-4">
                  {title}
                </h3>
              )}
              {content.map((para, pIdx) => (
                <p key={pIdx} className="text-white/50 leading-relaxed mb-4">{para}</p>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen pt-24 pb-24 bg-[#0A0A0A]" data-testid="product-detail-page">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        image={product.image_url}
        type="product"
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Back Link */}
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 text-white/40 hover:text-gold transition-colors mb-12 text-sm tracking-wider uppercase"
          data-testid="back-to-shop"
        >
          <ArrowLeft size={16} />
          {t('back')}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Image */}
          <div className="relative flex justify-center items-center">
            <div className="relative">
              <img
                src={product.image_url}
                alt={name}
                className="max-h-[600px] w-auto"
              />
              {/* Glow effect for UV gin */}
              {product.id === 'oridzin-uv' && (
                <div className="absolute inset-0 bg-purple-500/10 blur-[80px] -z-10" />
              )}
            </div>
          </div>

          {/* Details */}
          <div className="py-8">
            <div className="decorative-line mb-8" />
            <p className="text-gold text-xs tracking-[0.4em] uppercase mb-4">
              {t('products_subtitle')}
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-4">
              {name}
            </h1>
            <p className="text-white/50 text-lg mb-8">
              {description}
            </p>

            {/* Price & ABV */}
            <div className="flex items-baseline gap-4 mb-10 pb-10 border-b border-white/10">
              <span className="text-gold text-4xl font-heading">
                {formatPrice(product.price)}
              </span>
              <span className="text-white/40">RSD</span>
              <span className="text-white/30 ml-auto text-sm">
                {product.abv}% vol.
              </span>
            </div>

            {/* Quantity & Add to Cart */}
            <div className="flex items-center gap-6 mb-12">
              <div className="flex items-center border border-white/10">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-4 py-3 text-white/50 hover:text-white transition-colors"
                  data-testid="decrease-quantity"
                >
                  <Minus size={16} />
                </button>
                <span className="px-6 py-3 text-white min-w-[60px] text-center">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="px-4 py-3 text-white/50 hover:text-white transition-colors"
                  data-testid="increase-quantity"
                >
                  <Plus size={16} />
                </button>
              </div>
              <button
                onClick={handleAddToCart}
                className="flex-1 flex items-center justify-center gap-3 bg-gold hover:bg-gold-light text-black py-4 text-sm tracking-[0.15em] uppercase font-medium transition-colors duration-300"
                data-testid="add-to-cart-button"
              >
                <ShoppingBag size={18} />
                {t('add_to_cart')}
              </button>
            </div>

            {/* Quick Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
              <div className="bg-surface/50 p-4 border border-white/5">
                <p className="text-gold text-xs tracking-wider uppercase mb-2">{t('aroma')}</p>
                <p className="text-white/50 text-sm">{aroma}</p>
              </div>
              <div className="bg-surface/50 p-4 border border-white/5">
                <p className="text-gold text-xs tracking-wider uppercase mb-2">{t('taste')}</p>
                <p className="text-white/50 text-sm">{taste}</p>
              </div>
              <div className="bg-surface/50 p-4 border border-white/5">
                <p className="text-gold text-xs tracking-wider uppercase mb-2">{t('serving')}</p>
                <p className="text-white/50 text-sm">{serving}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Full Description Section - Below the fold */}
        {longDescription && (
          <div className="mt-20 pt-20 border-t border-white/10">
            <div className="max-w-3xl mx-auto">
              <div className="decorative-line mx-auto mb-8" />
              {renderLongDescription()}
            </div>
          </div>
        )}

        {/* FAQ Section */}
        {faqItems && (
          <div className="mt-20 pt-20 border-t border-white/10">
            <div className="max-w-3xl mx-auto">
              <div className="decorative-line mx-auto mb-8" />
              <p className="text-gold text-xs tracking-[0.4em] uppercase mb-4 text-center">
                {language === 'sr' ? 'Često postavljana pitanja' : 'Frequently Asked Questions'}
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-white mb-10 text-center">
                FAQ – {name}
              </h2>
              <div className="space-y-0">
                {faqItems.map((faq, index) => (
                  <FAQItem key={index} question={faq.question} answer={faq.answer} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
